/**
 * Audit OO2 — Cockpit-Block für Watchlist-Einträge mit offener Position.
 *
 * Kompakt, datenfokussiert. Zeigt:
 *   - Status-Pill (next_action)
 *   - aktueller R-Wert + Distanzen zu Stop / nächstem TP
 *   - 1–2 Klartext-Hinweise mit konkreten Zahlen
 *   - TP-Hit-Markierungen
 */

import { ChevronRight } from 'lucide-react';

import type { Position } from '@/types';
import { derivePositionMgmt, ACTION_PILL_CLASS, ACTION_LABEL } from '@/lib/position-mgmt';
import { cn } from '@/lib/utils';
import { HoverTooltip } from '@/components/ui/hover-tooltip';

interface Props {
  position: Position;
}

function fmtPct(v: number | null, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return '–';
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)} %`;
}

export function PositionMgmtBlock({ position }: Props) {
  const mgmt = derivePositionMgmt(position);
  if (!mgmt) {
    return (
      <div className="mt-2 rounded-md border border-zinc-800/60 bg-zinc-950/50 px-3 py-2 text-[11px] text-zinc-500">
        Position offen — Live-Kurs wird geladen.
      </div>
    );
  }

  const r = position.current_r_multiple;
  const distStop = mgmt.distance_to_stop_pct;
  const distTp = mgmt.distance_to_next_tp_pct;

  const stopBorder =
    distStop !== null && distStop < 1.5
      ? 'border-red-900/50'
      : distStop !== null && distStop < 4
        ? 'border-amber-900/40'
        : 'border-zinc-800/60';

  return (
    <div
      className={cn(
        'mt-2 rounded-md border bg-zinc-950/40 p-2.5 space-y-1.5',
        stopBorder
      )}
    >
      {/* Top-Row: Status-Pill + Kennzahlen */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-medium',
              ACTION_PILL_CLASS[mgmt.next_action]
            )}
          >
            {ACTION_LABEL[mgmt.next_action]}
          </span>
          {position.tp1_hit && (
            <span className="rounded bg-emerald-950/40 border border-emerald-800/40 px-1.5 py-0.5 text-[10px] text-emerald-300">
              TP1 ✓
            </span>
          )}
          {position.tp2_hit && (
            <span className="rounded bg-emerald-950/40 border border-emerald-800/40 px-1.5 py-0.5 text-[10px] text-emerald-300">
              TP2 ✓
            </span>
          )}
          {mgmt.stop_moved_to_breakeven && (
            <span className="rounded bg-blue-950/40 border border-blue-800/40 px-1.5 py-0.5 text-[10px] text-blue-300">
              Stop BE
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono">
          {r !== null && (
            <HoverTooltip
              widthRem={18}
              content={
                <div className="text-zinc-200">
                  Aktuelles R-Multiple. +1R = Gewinn = geplantes Risiko,
                  −1R = voller Stop.
                </div>
              }
            >
              <span
                className={cn(
                  r > 0.1 ? 'text-emerald-300' : r < -0.1 ? 'text-red-300' : 'text-zinc-300'
                )}
              >
                {r >= 0 ? '+' : ''}
                {r.toFixed(2)} R
              </span>
            </HoverTooltip>
          )}
          <HoverTooltip
            widthRem={14}
            content={
              <div className="text-zinc-200">
                Distanz vom aktuellen Kurs zum Stop-Loss.
              </div>
            }
          >
            <span
              className={cn(
                distStop !== null && distStop < 1.5
                  ? 'text-red-300'
                  : distStop !== null && distStop < 4
                    ? 'text-amber-300'
                    : 'text-zinc-400'
              )}
            >
              Stop: {fmtPct(distStop)}
            </span>
          </HoverTooltip>
          {mgmt.next_tp_label && distTp !== null && (
            <HoverTooltip
              widthRem={16}
              content={
                <div className="text-zinc-200">
                  Distanz zum naechsten Take-Profit ({mgmt.next_tp_label}).
                </div>
              }
            >
              <span className={cn(distTp < 2 ? 'text-emerald-300' : 'text-zinc-400')}>
                {mgmt.next_tp_label}: {fmtPct(distTp)}
              </span>
            </HoverTooltip>
          )}
          {!mgmt.next_tp_label && (
            <HoverTooltip
              widthRem={14}
              content={
                <div className="text-zinc-200">
                  Alle TPs erreicht — Runner.
                </div>
              }
            >
              <span className="text-emerald-300/80">Alle TPs ✓</span>
            </HoverTooltip>
          )}
        </div>
      </div>

      {/* Hinweise */}
      <ul className="space-y-0.5">
        {mgmt.hints.map((h, i) => (
          <li
            key={i}
            className="flex items-start gap-1 text-[11px] text-zinc-300 leading-snug"
          >
            <ChevronRight className="size-3 text-zinc-500 mt-0.5 shrink-0" />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {/* Optionaler Trade-Progress-Balken */}
      {mgmt.trade_progress_pct !== null && mgmt.next_tp_label && (
        <div className="pt-1">
          <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-0.5">
            <span>Entry</span>
            <span>{mgmt.trade_progress_pct.toFixed(0)} % bis {mgmt.next_tp_label}</span>
            <span>{mgmt.next_tp_label}</span>
          </div>
          <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                mgmt.trade_progress_pct > 70
                  ? 'bg-emerald-500/70'
                  : mgmt.trade_progress_pct > 30
                    ? 'bg-blue-500/70'
                    : 'bg-zinc-500/70'
              )}
              style={{ width: `${mgmt.trade_progress_pct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
